// ============================================================================
// Shopee — Stock updates (update_stock).
//
// Endpoint: `POST /api/v2/product/update_stock`.
//
// Payload shape (per Shopee v2 docs):
//   {
//     "item_id": 12345,
//     "stock_list": [
//       { "model_id": 0, "seller_stock": [{ "stock": 5 }] },  // item-level
//       { "model_id": 789, "seller_stock": [{ "stock": 3 }] } // variant
//     ]
//   }
//
// Constraints:
//   - ONE `item_id` per request.
//   - `stock_list` can carry BOTH the item-level stock (model_id=0) and any
//     number of variant rows in one call.
//   - `updateShopeeStockBatch` groups updates by `item_id` internally so N
//     variants on the same item collapse to ONE API call. Total parallelism
//     is still capped to Shopee's documented 50 items per batch.
// ============================================================================

import { shopeeApiCall } from './client'
import { ShopeeError, type ShopeeErrorEnvelope } from './types'

// ============================================================================
// Auth + constants
// ============================================================================

export interface ShopeeAuthContext {
  partnerId: string | number
  partnerKey: string
  accessToken: string
  shopId: string | number
  apiBase?: string
  fetchImpl?: typeof fetch
}

/** Shopee's documented batch cap on distinct item_ids processed per sweep. */
export const SHOPEE_STOCK_BATCH_MAX = 50

// ============================================================================
// updateShopeeStock — single item (optionally with variants)
// ============================================================================

export interface UpdateShopeeStockInput extends ShopeeAuthContext {
  itemId: number | string
  /**
   * One or more rows; typically one per (variant_id, stock) pair. `modelId=0`
   * or omitted → the item-level stock. Positive integers only.
   */
  stockList: Array<{ modelId?: number | string; stock: number }>
}

/**
 * Update stock on ONE item (optionally including its variants). Shopee's
 * endpoint accepts the full `stock_list` atomically; partial failures are
 * reported back on individual `result` rows but we surface them as a single
 * `ShopeeError` when ANY row failed (tracked via the `failure_list` array in
 * the response).
 */
export async function updateShopeeStock(
  input: UpdateShopeeStockInput,
): Promise<void> {
  if (!Array.isArray(input.stockList) || input.stockList.length === 0) {
    throw new ShopeeError('INVALID_INPUT', 'stockList must be a non-empty array')
  }

  const body = {
    item_id: toItemNumber(input.itemId),
    stock_list: input.stockList.map((row) => ({
      model_id:
        typeof row.modelId === 'number'
          ? row.modelId
          : typeof row.modelId === 'string' && row.modelId.length > 0
            ? Number(row.modelId) || 0
            : 0,
      seller_stock: [{ stock: Math.max(0, Math.floor(row.stock)) }],
    })),
  }

  const apiInput: Parameters<
    typeof shopeeApiCall<
      {
        failure_list?: Array<{ item_id?: number; model_id?: number; failed_reason?: string }>
      } & ShopeeErrorEnvelope
    >
  >[0] = {
    apiPath: '/api/v2/product/update_stock',
    method: 'POST',
    partnerId: input.partnerId,
    partnerKey: input.partnerKey,
    accessToken: input.accessToken,
    shopId: input.shopId,
    body,
    errorHint: 'item',
    ...(input.apiBase ? { apiBase: input.apiBase } : {}),
    ...(input.fetchImpl ? { fetchImpl: input.fetchImpl } : {}),
  }
  const resp = await shopeeApiCall<
    {
      failure_list?: Array<{ item_id?: number; model_id?: number; failed_reason?: string }>
    } & ShopeeErrorEnvelope
  >(apiInput)

  if (Array.isArray(resp.failure_list) && resp.failure_list.length > 0) {
    const first = resp.failure_list[0]
    throw new ShopeeError(
      'PRODUCT_SYNC_FAILED',
      `Shopee rejected stock update for item ${first.item_id ?? '?'} model ${first.model_id ?? '?'}: ${first.failed_reason ?? 'unknown'}`,
      { providerMessage: first.failed_reason },
    )
  }
}

// ============================================================================
// updateShopeeStockBatch — collapse N variants per item into one call
// ============================================================================

export interface UpdateShopeeStockBatchInput extends ShopeeAuthContext {
  updates: Array<{ productId: string; variantId?: string; quantity: number }>
}

export interface UpdateShopeeStockBatchOutput {
  succeeded: number
  failed: Array<{ productId: string; variantId?: string; error: string }>
}

/**
 * Apply many stock updates, batching by `item_id` (one API call per item,
 * with all variants of that item in one `stock_list`). Capped at
 * `SHOPEE_STOCK_BATCH_MAX` distinct items per sweep — additional items fall
 * through to subsequent sweeps.
 *
 * Returns a summary of successes + per-row failures. Individual API errors
 * are caught and rolled up so one bad SKU doesn't abort the whole batch.
 */
export async function updateShopeeStockBatch(
  input: UpdateShopeeStockBatchInput,
): Promise<UpdateShopeeStockBatchOutput> {
  if (!Array.isArray(input.updates)) {
    throw new ShopeeError('INVALID_INPUT', 'updates must be an array')
  }

  // Group by product id, de-duping same `(productId, variantId)` so last-write
  // wins deterministically (Round 1 H4 fix). Prior impl would push both rows
  // into the `arr`, and Shopee's behaviour on duplicate `model_id` in a single
  // `stock_list` is undefined (observed: last-one-wins in practice, but fail
  // closed here instead of relying on it).
  //
  // Key: `productId:variantId` ("" for item-level rows). Map preserves
  // insertion order so iteration order below is stable.
  const byProductKey = new Map<
    string,
    Map<string, { productId: string; variantId?: string; quantity: number }>
  >()
  for (const u of input.updates) {
    if (!u || typeof u.productId !== 'string' || u.productId.length === 0) continue
    if (typeof u.quantity !== 'number' || !Number.isFinite(u.quantity)) continue
    const variantKey = u.variantId ?? ''
    const perProduct = byProductKey.get(u.productId) ?? new Map()
    // Last-write-wins on duplicate `(productId, variantId)`. Set() overwrites.
    perProduct.set(variantKey, {
      productId: u.productId,
      ...(u.variantId ? { variantId: u.variantId } : {}),
      quantity: u.quantity,
    })
    byProductKey.set(u.productId, perProduct)
  }

  // Flatten back into the shape the inner call expects.
  const byProduct = new Map<string, Array<{ variantId?: string; quantity: number }>>()
  for (const [productId, variants] of byProductKey) {
    const rows: Array<{ variantId?: string; quantity: number }> = []
    for (const v of variants.values()) {
      rows.push(
        v.variantId
          ? { variantId: v.variantId, quantity: v.quantity }
          : { quantity: v.quantity },
      )
    }
    byProduct.set(productId, rows)
  }

  const result: UpdateShopeeStockBatchOutput = { succeeded: 0, failed: [] }
  const productIds = Array.from(byProduct.keys())

  // Process in sweeps of SHOPEE_STOCK_BATCH_MAX items.
  for (let i = 0; i < productIds.length; i += SHOPEE_STOCK_BATCH_MAX) {
    const sweep = productIds.slice(i, i + SHOPEE_STOCK_BATCH_MAX)
    for (const productId of sweep) {
      const rows = byProduct.get(productId)!
      try {
        await updateShopeeStock({
          partnerId: input.partnerId,
          partnerKey: input.partnerKey,
          accessToken: input.accessToken,
          shopId: input.shopId,
          ...(input.apiBase ? { apiBase: input.apiBase } : {}),
          ...(input.fetchImpl ? { fetchImpl: input.fetchImpl } : {}),
          itemId: productId,
          stockList: rows.map((r) => ({
            ...(r.variantId ? { modelId: r.variantId } : {}),
            stock: r.quantity,
          })),
        })
        result.succeeded += rows.length
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        for (const r of rows) {
          result.failed.push({
            productId,
            ...(r.variantId ? { variantId: r.variantId } : {}),
            error: msg,
          })
        }
      }
    }
  }
  return result
}

// ============================================================================
// Helpers
// ============================================================================

function toItemNumber(v: number | string): number {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.floor(v)
  if (typeof v === 'string' && v.length > 0) {
    const n = Number(v)
    if (Number.isFinite(n)) return Math.floor(n)
  }
  throw new ShopeeError('INVALID_INPUT', 'itemId must be a finite number or numeric string')
}
